import React, { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { FilePlus, ChevronDown, ChevronUp } from "lucide-react"
import {
	useGenerateCMEReport, // Use CME hooks
	useCMEReportsForCase,
	useDeleteCMEReport,
} from "../../hooks/useCMEReports" // Point to the new hook file
import { toast } from "sonner"
import { ClipLoader } from "react-spinners"
import CMEReportItem from "./CMEReportItem" // Import the new item component
import { AnimatePresence, motion } from "framer-motion"

interface CMEReportsListProps {
	caseId: string
}

const CMEReportsList: React.FC<CMEReportsListProps> = ({ caseId }) => {
	const [showAllReports, setShowAllReports] = useState(false)

	// Fetch CME reports using the specific hook
	const {
		data: cmeReports,
		isLoading,
		isError,
		refetch,
	} = useCMEReportsForCase(caseId)

	// Use mutations for CME report actions
	const generateCMEReportMutation = useGenerateCMEReport()
	const deleteCMEReportMutation = useDeleteCMEReport()

	// Polling logic remains similar, watches cmeReports
	useEffect(() => {
		let intervalId: NodeJS.Timeout | null = null
		if (!isLoading && !isError && cmeReports) {
			const shouldPoll = cmeReports.some(
				(report) =>
					report.status === "pending" || report.status === "in_progress"
			)
			if (shouldPoll) {
				intervalId = setInterval(() => {
					refetch()
				}, 1000)
			}
		}
		return () => {
			if (intervalId) clearInterval(intervalId)
		}
	}, [cmeReports, isLoading, isError, refetch])

	// Handler to generate a new CME report
	const handleGenerateCMEReport = async () => {
		try {
			await generateCMEReportMutation.mutateAsync(caseId)
			toast.success("CME report generation started")
			refetch()
		} catch (error) {
			toast.error("Failed to start CME report generation")
		}
	}

	// Handler to delete a CME report
	const handleDeleteCMEReport = async (reportId: string) => {
		try {
			await deleteCMEReportMutation.mutateAsync({ caseId, reportId })
			toast.success("CME report deleted")
			refetch()
		} catch (error) {
			toast.error("Failed to delete CME report")
		}
	}

	if (isLoading && !cmeReports)
		return <div className="animate-pulse">Loading CME reports...</div>
	if (isError)
		return <div className="text-destructive">Error loading CME reports</div>

	// Sort CME reports by creation date
	const sortedReports = cmeReports
		? [...cmeReports].sort(
				(a, b) =>
					new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
		  )
		: []

	const mostRecentReport = sortedReports.length > 0 ? sortedReports[0] : null
	const hasMoreReports = sortedReports.length > 1

	return (
		<div className="space-y-4">
			{/* Button to generate CME report */}
			<Button
				onClick={handleGenerateCMEReport}
				disabled={generateCMEReportMutation.isPending}
				className="w-full sm:w-auto"
				variant={sortedReports.length > 0 ? "outline" : "default"}
				size={sortedReports.length > 0 ? "sm" : "default"}
			>
				{generateCMEReportMutation.isPending ? (
					<>
						<ClipLoader size={14} color="#fff" className="mr-2" />
						Generating...
					</>
				) : (
					<>
						<FilePlus className="h-4 w-4 mr-2" />
						{sortedReports.length > 0
							? "Generate Another CME Report" // Updated text
							: "Generate New CME Report"}{" "}
						{/* Updated text */}
					</>
				)}
			</Button>

			{/* List of CME reports with animation */}
			<AnimatePresence>
				{sortedReports.length > 0 && (
					<motion.div
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -20 }}
						transition={{ duration: 0.5 }}
						className="border rounded-lg p-4 bg-background"
						layout
					>
						<motion.div layout className="flex flex-col">
							<AnimatePresence mode="popLayout">
								{/* Most recent report */}
								{mostRecentReport && (
									<motion.div
										key={mostRecentReport.id}
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{ opacity: 0, scale: 0.95 }}
										transition={{ duration: 0.3 }}
										className="mb-2"
										layout
									>
										<CMEReportItem // Use CMEReportItem component
											report={mostRecentReport}
											caseId={caseId}
											onDelete={handleDeleteCMEReport} // Pass CME delete handler
										/>
									</motion.div>
								)}

								{/* Additional reports (if expanded) */}
								{showAllReports &&
									sortedReports.length > 1 &&
									sortedReports.slice(1).map((report) => (
										<motion.div
											key={report.id}
											initial={{ opacity: 0, height: 0 }}
											animate={{ opacity: 1, height: "auto" }}
											exit={{ opacity: 0, height: 0 }}
											transition={{ duration: 0.3 }}
											className="mb-2"
											layout
										>
											<CMEReportItem // Use CMEReportItem component
												report={report}
												caseId={caseId}
												onDelete={handleDeleteCMEReport} // Pass CME delete handler
											/>
										</motion.div>
									))}
							</AnimatePresence>

							{/* Button to show/hide older reports */}
							{hasMoreReports && (
								<motion.div layout>
									<Button
										variant="ghost"
										size="sm"
										className="w-full text-muted-foreground mt-2"
										onClick={() => setShowAllReports(!showAllReports)}
									>
										{showAllReports ? (
											<>
												<ChevronUp className="h-4 w-4 mr-1" />
												Show less
											</>
										) : (
											<>
												<ChevronDown className="h-4 w-4 mr-1" />
												Show {sortedReports.length - 1} more{" "}
												{sortedReports.length - 1 === 1 ? "report" : "reports"}
											</>
										)}
									</Button>
								</motion.div>
							)}
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	)
}

export default CMEReportsList
